import { BLUE } from '../Theme/palette';
import { OrderVehicleReminder } from '@components';
import {
  useServiceHistory,
  useServiceReminders,
  useRoleAccess,
} from '@hooks';
import { OrderStatus } from '@models';
import { CreateServiceHistory, Vehicle } from '@services';
import { useStore } from '@stores';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Typography,
} from 'antd';
import { DashboardOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useView } from '@hooks';
import './ServiceHistoryModal.css';

const { Text } = Typography;

declare const abp: any;

interface IProps {
  id?: string;
  open: boolean;
  orderId: string;
  mileage: number | undefined;
  vin: string;
  /** Used to find the vehicle when the order has no VIN. */
  registrationNumber?: string;
  customerId?: string;
  onCancel: () => void;
  /** Called once the job has been saved as Completed (before the reminders step, if any). */
  onCompleted?: () => void;
  setOpen: (open: boolean) => void;
}

export const ServiceHistoryModal: React.FC<IProps> = observer(
  ({ id, open, orderId, mileage, vin, registrationNumber, customerId, onCancel, onCompleted, setOpen }) => {
    const { isMobile } = useView();
    const { orderStore, serviceHistoryStore } = useStore();
    const [form] = Form.useForm();
    const [showRemindersForm, setShowRemindersForm] = React.useState(false);

    const { serviceHistoryReminders } = useRoleAccess().order;

    const { reminders, loading } = useServiceReminders();

    const { serviceHistory } = useServiceHistory(id);

    // Looked up on open by VIN, or by plate when the order has none. Not read from the shared
    // store, which may hold another screen's vehicle.
    const { vehicleStore } = useStore();
    const [vehicle, setVehicle] = useState<Vehicle | undefined>();
    useEffect(() => {
      if (!open || !(vin || registrationNumber)) return undefined;
      let cancelled = false;
      vehicleStore
        .getByVinOrRegistration(vin, registrationNumber)
        .then((result) => !cancelled && setVehicle(result))
        .catch(() => undefined);
      return () => {
        cancelled = true;
        setVehicle(undefined);
      };
    }, [open, vin, registrationNumber, vehicleStore]);

    const allowUpdate = serviceHistory
      ? serviceHistory.tenantId === abp.session.tenantId &&
        serviceHistory.creationTime &&
        dayjs().diff(dayjs(serviceHistory.creationTime), 'month', true) < 1
      : true;

    const reminderServiceIds = id
      ? Array.from(new Set(serviceHistory?.reminderServices.map((x) => x.id)))
      : [];

    useEffect(() => {
      form.setFieldsValue({
        id,
        mileage,
        orderId,
        status: OrderStatus.Completed,
        vin: vehicle?.combinedVin,
        registrationNumber: vehicle?.registrationNumber,
        reminderServiceIds,
      });
      mileage && form.validateFields(['mileage']);
    }, [form, vehicle, reminderServiceIds, mileage, orderId, id]);

    const handleUpdate = useCallback(
      async (values: CreateServiceHistory) => {
        if (!vehicle) return;

        const updateSuccess = await orderStore.updateStatus({
          id: values.orderId,
          mileage: values.mileage,
          status: !id ? OrderStatus.Completed : undefined,
        });

        if (!updateSuccess) return;

        onCompleted?.();

        if (!serviceHistory) {
          const { status, ...historyPayload } = values as any;
          await serviceHistoryStore.create(historyPayload);
        } else {
          await serviceHistoryStore.update({
            ...values,
            id: serviceHistory.id,
          });
          form.setFieldsValue({
            reminderServiceIds: serviceHistory.reminderServices.map((x) => x.id),
          });
        }

        if (serviceHistoryReminders) {
          setShowRemindersForm(true);
        } else {
          handleCancel();
        }
      },
      [form, vehicle, serviceHistory, serviceHistoryReminders]
    );

    const handleCancel = () => {
      setOpen(false);
      onCancel();
      setShowRemindersForm(false);
    };

    const footer = !showRemindersForm
      ? allowUpdate && (
          <Row justify="end" gutter={8}>
            <Col>
              <Button onClick={handleCancel}>Cancel</Button>
            </Col>
            <Col>
              <Button
                loading={orderStore.statusLoading || serviceHistoryStore.loading}
                disabled={orderStore.statusLoading || serviceHistoryStore.loading}
                type="primary"
                onClick={() => form.submit()}
              >
                Save & continue
              </Button>
            </Col>
          </Row>
        )
      : (
          <Row justify="end">
            <Col>
              <Button type="primary" onClick={handleCancel}>
                Complete
              </Button>
            </Col>
          </Row>
        );

    return (
      <Drawer
        destroyOnHidden
        title={showRemindersForm ? 'Update reminders' : 'Service history'}
        open={open}
        loading={loading || serviceHistoryStore.loading}
        onClose={handleCancel}
        size={isMobile ? '100%' : 1200}
        footer={footer}
      >
        {!showRemindersForm ? (
          <Form
            form={form}
            scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
            onFinish={handleUpdate}
          >
            <Form.Item name="id" hidden>
              <Input size="small" />
            </Form.Item>
            <Form.Item name="orderId" hidden>
              <Input size="small" />
            </Form.Item>
            <Form.Item name="status" hidden>
              <Input size="small" />
            </Form.Item>
            <Form.Item name="vin" hidden>
              <Input size="small" />
            </Form.Item>
            <Form.Item name="registrationNumber" hidden>
              <Input size="small" />
            </Form.Item>
            <Card
              size="small"
              style={{ marginBottom: 16, background: '#fafafa' }}
            >
              {vehicle?.mileage != null && (
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                  Last recorded mileage: {vehicle.mileage.toLocaleString()}
                </Text>
              )}
              <Form.Item
                name="mileage"
                required
                style={{ marginBottom: 0 }}
                rules={[
                  { required: true, message: 'Mileage is required' },
                  {
                    type: 'number',
                    max: 1000000,
                    warningOnly: true,
                    message: `Maximum allowed mileage is ${'1000000'.toLocaleString()}.`,
                    transform: (value) => Number(value),
                  },
                  {
                    type: 'number',
                    min: vehicle?.mileage || 0,
                    warningOnly: true,
                    message: `Entered mileage is lower than the last recorded value.`,
                    transform: (value) => Number(value),
                  },
                ]}
              >
                <InputNumber
                  type="number"
                  disabled={!allowUpdate}
                  style={{ width: isMobile ? '100%' : 220 }}
                  controls={false}
                  changeOnWheel={false}
                  size="large"
                  placeholder="Enter mileage"
                  prefix={<DashboardOutlined style={{ color: BLUE.badge }} />}
                  formatter={(value) => (value ? `${value}`.replace(/\./g, '') : '')}
                  parser={(value) => (value ? value.replace(/\./g, '') : '')}
                />
              </Form.Item>
            </Card>

            <Row gutter={[16, 16]}>
              <Col span={isMobile ? 24 : 6}>
                <Card
                  size="small"
                  title="Job details"
                  styles={{ header: { background: '#fafafa' } }}
                  style={{ height: '100%' }}
                >
                  {orderStore.order?.serviceOrders.map((serviceOrder) => (
                    <div key={serviceOrder.id} style={{ marginBottom: 8 }}>
                      <Text strong>{serviceOrder.title}</Text>
                      {serviceOrder.extras.length > 0 && (
                        <div style={{ marginLeft: 12, marginTop: 2 }}>
                          {serviceOrder.extras.map((extra) => (
                            <Text key={extra.id} type="secondary" style={{ display: 'block', fontSize: 13 }}>
                              {extra.name}
                            </Text>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </Card>
              </Col>

              <Col span={isMobile ? 24 : 18}>
                <Card
                  size="small"
                  title="Record service history"
                  styles={{ header: { background: '#fafafa' }, body: { padding: '16px 16px 28px' } }}
                  style={{ height: '100%', overflow: 'hidden' }}
                >
                  <Form.Item name="reminderServiceIds" style={{ marginBottom: 4 }} className="service-history-checkboxes">
                    <Checkbox.Group style={{ width: '100%' }} disabled={loading || !allowUpdate}>
                      {Object.keys(reminders).length > 0 ? (
                        <Row gutter={[12, 12]}>
                          {Object.entries(reminders).map(([_key, reminder]) => (
                            <Col key={reminder.id} xs={24} sm={12} md={8}>
                              <div
                                style={{
                                  border: '1px solid #f0f0f0',
                                  borderRadius: 6,
                                  padding: '10px 12px',
                                  background: '#fff',
                                  height: '100%',
                                }}
                              >
                                <div
                                  style={{
                                    background: '#fafafa',
                                    margin: '-10px -12px 8px',
                                    padding: '6px 12px',
                                    borderBottom: '1px solid #f0f0f0',
                                    borderRadius: '6px 6px 0 0',
                                  }}
                                >
                                  <Text strong style={{ fontSize: 13 }}>{reminder.title}</Text>
                                </div>
                                {Object.entries(reminder.reminderServices).map(
                                  ([_reminderServiceId, reminderService]) => (
                                    <div key={reminderService.id} style={{ marginBottom: 2 }}>
                                      <Checkbox value={reminderService.id}>
                                        {reminderService.name}
                                      </Checkbox>
                                    </div>
                                  )
                                )}
                              </div>
                            </Col>
                          ))}
                        </Row>
                      ) : (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="No service history items available"
                        />
                      )}
                    </Checkbox.Group>
                  </Form.Item>
                </Card>
              </Col>
            </Row>
          </Form>
        ) : (
          <OrderVehicleReminder
            vin={vehicle?.combinedVin || ''}
            customerId={customerId || ''}
            registrationNumber={vehicle?.registrationNumber || ''}
            currentMileage={form.getFieldValue('mileage')}
          />
        )}
      </Drawer>
    );
  }
);

